import { CoreModule } from '@abp/ng.core';
import { patchOAuthUserProfile } from './core/oauth-patch';
import { OAuthService } from 'angular-oauth2-oidc';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { ThemeSharedModule } from '@abp/ng.theme.shared';
import {ThemeLeptonXModule, HttpErrorComponent} from '@volosoft/abp.ng.theme.lepton-x';
import { APP_INITIALIZER, Injectable, NgModule } from '@angular/core';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsModule } from '@ngxs/store';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { IdentityServerConfigModule } from '@volo/abp.ng.identity-server/config';
import { IdentityConfigModule } from '@volo/abp.ng.identity/config';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
//import { HttpErrorComponent, ThemeLeptonModule } from '@volo/abp.ng.theme.lepton';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MatDialogModule } from '@angular/material/dialog';
import { IdentityUserDelegationService } from '@volo/abp.ng.account/public/proxy';
import { IdentityUserDelegationServiceStub } from './hacks/identity-user-delegation-stub';
import { CommercialUiConfigModule } from '@volo/abp.commercial.ng.ui/config';
import { AbpOAuthModule } from '@abp/ng.oauth';

/* ✨ NEW: for trimming the user menu */
import { UserMenuService } from '@abp/ng.theme.shared';
import { eUserMenuItems } from '@volosoft/abp.ng.theme.lepton-x';

/* route providers */
import { APP_ROUTE_PROVIDER } from './route.provider';
import { ARTCAST_GALLERIES_ROUTE_PROVIDER } from './galleries/galleries-route.provider';
import { ARTCAST_ARTWORKS_ROUTE_PROVIDER } from './artworks/artworks-route.provider';
import { ARTCAST_COMMENTS_ROUTE_PROVIDER } from './comments/comments-route.provider';
import { ARTCAST_CATEGORIES_ROUTE_PROVIDER } from './categories/categories-route.provider';
import { ARTCAST_REV_SHARE_PERIODS_ROUTE_PROVIDER } from './ledgers/rev-share-periods-route.provider';
import { ARTCAST_LEDGER_ITEMS_ROUTE_PROVIDER } from './ledgers/ledger-items-route.provider';
import { ARTCAST_OFFERS_ROUTE_PROVIDER } from './products/offers-route.provider';
import { ARTCAST_FLAGS_ROUTE_PROVIDER } from './flags/flags-route.provider';
import { ARTCAST_NOTIFICATIONS_ROUTE_PROVIDER } from './notifications/notification-route.provider';
import { ARTCAST_TOPICNOTIFICATIONS_ROUTE_PROVIDER } from './notifications/topic-notifications/topic-notification-route.provider';
import { ARTCAST_CACHES_ROUTE_PROVIDER } from './caches/cache-route.provider';
import { ARTCAST_API_CLIENTS_ROUTE_PROVIDER } from './api-clients/api-clients-route.provider';
import { ARTCAST_MY_API_ACCESS_ROUTE_PROVIDER } from './my-api-access/my-api-access-route.provider';
import { ARTCAST_USER_GALLERIES_ROUTE_PROVIDER } from './user-galleries/user-galleries-route.provider';
import { ARTCAST_USER_ARTWORKS_ROUTE_PROVIDER } from './user-artworks/user-artworks-route.provider';
import { USER_SHARE_ROUTE_PROVIDER } from './user-share/user-share-route.provider';
import { GALLERY_SHARE_ROUTE_PROVIDER } from './gallery-share/gallery-share-route.provider';
import { ARTWORK_SHARE_ROUTE_PROVIDER } from './artwork-share/artwork-share-route.provider';
import { IdentityUserService } from '@volo/abp.ng.identity/proxy';
import { ExtendedIdentityUserServiceOverride } from './identity-config/extended-identity-user-service';

export const ensureNavigatorLang = () => () => {
  if (!navigator.language) (navigator as any).language = 'en';
};

@Injectable({ providedIn: 'root' })
export class NoopLocalStorageListenerService {}

/* NEW: Only keep My account + Log out */
export function hideUserMenuItems(userMenu: UserMenuService) {
  return () => {
    userMenu.removeItem(eUserMenuItems.Sessions);
    userMenu.removeItem(eUserMenuItems.ExternalLogins);
    userMenu.removeItem(eUserMenuItems.LinkedAccounts);
    userMenu.removeItem(eUserMenuItems.AuthorityDelegation);
    userMenu.removeItem(eUserMenuItems.SecurityLogs);
    // Keep MyAccount + Logout
  };
}

@NgModule({
  declarations: [],
  imports: [   
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    MatDialogModule,

    /* ABP / Lepton / Commercial modules */
    CoreModule.forRoot({ environment, registerLocaleFn: registerLocale() }),
    ThemeSharedModule.forRoot({
      httpErrorConfig: {
        errorScreen: {
          component: HttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      },
    }),
    AbpOAuthModule.forRoot(),
    IdentityConfigModule.forRoot(),
    CommercialUiConfigModule.forRoot({ enableFlagIcon: true }),
    // <- new LeptonX theming
    ThemeLeptonXModule.forRoot(),

    // <- this actually turns on the side‐menu (with titles)
    SideMenuLayoutModule.forRoot(),
    //ThemeLeptonModule.forRoot(),
    LanguageManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    IdentityServerConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),

    /* state */
    NgxsModule.forRoot([])
  ],

  providers: [   
    {
      provide: APP_INITIALIZER,
      useFactory: patchOAuthUserProfile,
      deps: [OAuthService],
      multi: true,
    },
    /* ✨ NEW: trim user menu at startup */
    {
      provide: APP_INITIALIZER,
      useFactory: hideUserMenuItems,
      deps: [UserMenuService],
      multi: true,
    },

    APP_ROUTE_PROVIDER,
    ARTCAST_GALLERIES_ROUTE_PROVIDER,
    ARTCAST_ARTWORKS_ROUTE_PROVIDER,
    ARTCAST_COMMENTS_ROUTE_PROVIDER,
    ARTCAST_CATEGORIES_ROUTE_PROVIDER,
    ARTCAST_REV_SHARE_PERIODS_ROUTE_PROVIDER,
    ARTCAST_LEDGER_ITEMS_ROUTE_PROVIDER,
    ARTCAST_OFFERS_ROUTE_PROVIDER,
    ARTCAST_FLAGS_ROUTE_PROVIDER,
    ARTCAST_NOTIFICATIONS_ROUTE_PROVIDER,
    ARTCAST_TOPICNOTIFICATIONS_ROUTE_PROVIDER,
    ARTCAST_CACHES_ROUTE_PROVIDER,
    ARTCAST_API_CLIENTS_ROUTE_PROVIDER,
    ARTCAST_MY_API_ACCESS_ROUTE_PROVIDER,
    ARTCAST_USER_GALLERIES_ROUTE_PROVIDER,
    ARTCAST_USER_ARTWORKS_ROUTE_PROVIDER,
    USER_SHARE_ROUTE_PROVIDER,
    GALLERY_SHARE_ROUTE_PROVIDER,
    ARTWORK_SHARE_ROUTE_PROVIDER,
    { provide: IdentityUserDelegationService, useClass: IdentityUserDelegationServiceStub },
    { provide: IdentityUserService, useClass: ExtendedIdentityUserServiceOverride },
  ],

  //bootstrap: [AppComponent],
})
export class AppModule {}
